import { useState } from 'react';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import './FieldEditors.css';

// ---------------------------------------------------------------------------
// Genre chips
// ---------------------------------------------------------------------------

interface GenreChipsProps {
    values: string[];
    onChange: (next: string[]) => void;
    disabled?: boolean;
}

/**
 * Chip editor for the album's genre list. Adding is type-ahead-free by
 * design: genres are free text (providers disagree on naming), so a text
 * input plus Enter, and chips are removed individually.
 */
export function GenreChips({ values, onChange, disabled }: GenreChipsProps) {
    const [draft, setDraft] = useState('');

    const add = () => {
        const name = draft.trim();
        if (!name) return;
        // Case-insensitive dedupe: "rock" and "Rock" are one genre here too.
        if (values.some((v) => v.toLowerCase() === name.toLowerCase())) {
            setDraft('');
            return;
        }
        onChange([...values, name]);
        setDraft('');
    };

    return (
        <div className="fieldEditor">
            <div className="fieldEditorChips">
                {values.map((g, i) => (
                    <span className="fieldEditorChip" key={`${g}-${i}`}>
                        {g}
                        <button
                            type="button"
                            className="fieldEditorChipRemove"
                            disabled={disabled}
                            aria-label={`Remove ${g}`}
                            onClick={() => onChange(values.filter((_, j) => j !== i))}
                        >
                            <X size={12} strokeWidth={2.75} />
                        </button>
                    </span>
                ))}
                {!values.length && <span className="fieldEditorEmpty">No genres</span>}
            </div>
            <div className="fieldEditorAddRow">
                <input
                    type="text"
                    className="fieldEditorInput"
                    placeholder="Add a genre…"
                    value={draft}
                    disabled={disabled}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            add();
                        }
                    }}
                />
                <button type="button" className="fieldEditorAddButton" disabled={disabled || !draft.trim()} onClick={add}>
                    <Plus size={14} strokeWidth={2.75} />
                </button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Artist order
// ---------------------------------------------------------------------------

interface ArtistOrderProps {
    values: { id: number; name: string }[];
    onChange: (next: { id: number; name: string }[]) => void;
    disabled?: boolean;
}

/**
 * Ordered artist list. Position 0 is the main artist (what the album is filed
 * under); the arrows reorder, and a typed name creates the artist on the
 * server when saving. List order IS the meaning - there is no separate
 * "main artist" flag to keep in sync.
 */
export function ArtistOrder({ values, onChange, disabled }: ArtistOrderProps) {
    const [draft, setDraft] = useState('');

    const add = () => {
        const name = draft.trim();
        if (!name) return;
        if (values.some((a) => a.name.toLowerCase() === name.toLowerCase())) {
            setDraft('');
            return;
        }
        // New artists get id 0 - the server creates them on save.
        onChange([...values, { id: 0, name }]);
        setDraft('');
    };

    const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= values.length) return;
        const next = [...values];
        [next[i], next[j]] = [next[j], next[i]];
        onChange(next);
    };

    return (
        <div className="fieldEditor">
            <ul className="fieldEditorList">
                {values.map((a, i) => (
                    <li className="fieldEditorRow" key={`${a.id}-${a.name}-${i}`}>
                        <span className={`fieldEditorRank ${i === 0 ? 'fieldEditorRankMain' : ''}`}>
                            {i === 0 ? 'Main' : `#${i + 1}`}
                        </span>
                        <span className="fieldEditorName">{a.name}</span>
                        <span className="fieldEditorRowActions">
                            <button
                                type="button"
                                className="fieldEditorIconButton"
                                disabled={disabled || i === 0}
                                aria-label={`Make ${a.name} the main artist`}
                                onClick={() => move(i, -1)}
                            >
                                <ArrowUp size={13} strokeWidth={2.75} />
                            </button>
                            <button
                                type="button"
                                className="fieldEditorIconButton"
                                disabled={disabled || i === values.length - 1}
                                aria-label={`Move ${a.name} down`}
                                onClick={() => move(i, 1)}
                            >
                                <ArrowDown size={13} strokeWidth={2.75} />
                            </button>
                            <button
                                type="button"
                                className="fieldEditorIconButton"
                                disabled={disabled || values.length === 1}
                                aria-label={`Remove ${a.name}`}
                                onClick={() => onChange(values.filter((_, j) => j !== i))}
                            >
                                <X size={13} strokeWidth={2.75} />
                            </button>
                        </span>
                    </li>
                ))}
            </ul>
            <div className="fieldEditorAddRow">
                <input
                    type="text"
                    className="fieldEditorInput"
                    placeholder="Add an artist…"
                    value={draft}
                    disabled={disabled}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            add();
                        }
                    }}
                />
                <button type="button" className="fieldEditorAddButton" disabled={disabled || !draft.trim()} onClick={add}>
                    <Plus size={14} strokeWidth={2.75} />
                </button>
            </div>
            <span className="fieldEditorHint">The first artist is the main artist. Typed names are created on save if they don't exist.</span>
        </div>
    );
}
